import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import InvoiceForm from '../components/InvoiceForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

function InvoicesPage() {
    const [formOpen, setFormOpen] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Load user's company
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    // Load invoices for the company
    const { data: invoicesData, isLoading: invoicesLoading, isError, error } = useQuery(
        ['invoices', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadInvoices({ company: company.id });
        },
        {
            enabled: !!company?.id,
            keepPreviousData: true
        }
    );

    const invoices = invoicesData?.results || invoicesData || [];

    const handleAddClick = () => {
        setSelectedInvoiceId(null);
        setFormOpen(true);
    };

    const handleItemClick = (invoiceId) => {
        navigate(`/employer/invoices/${invoiceId}`);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setSelectedInvoiceId(null);
    };

    const handleInvoiceSaved = () => {
        queryClient.invalidateQueries(['invoices', company?.id]);
    };

    const handleSaveAndAddAnother = () => {
        queryClient.invalidateQueries(['invoices', company?.id]);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    if (companyLoading || invoicesLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (!company) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to create a company first. <Button href="/employer/my-company">Go to My Company</Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={600} gutterBottom>
                            Invoices
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage your company invoices
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Invoice
                    </Button>
                </Box>

                {isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error?.message || 'Failed to load invoices'}
                    </Alert>
                )}

                {invoices.length === 0 && !invoicesLoading && (
                    <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                        <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No invoices found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Start by adding your first invoice
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Add Invoice
                        </Button>
                    </Paper>
                )}

                {invoices.length > 0 && (
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <List>
                                {invoices.map((invoice, index) => {
                                    const employee = invoice.employee;
                                    const employeeName = employee 
                                        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                                        : 'Unknown Employee';
                                    const issueDate = invoice.issue_date 
                                        ? new Date(invoice.issue_date).toLocaleDateString()
                                        : '';
                                    const totalAmount = invoice.total_amount 
                                        ? `$${parseFloat(invoice.total_amount).toFixed(2)}`
                                        : '—';

                                    return (
                                        <React.Fragment key={invoice.id}>
                                            <ListItemButton onClick={() => handleItemClick(invoice.id)}>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body1" fontWeight={500}>
                                                                {employeeName}
                                                            </Typography>
                                                            {invoice.invoice_number && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    ({invoice.invoice_number})
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                                            {issueDate && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Issue: {issueDate}
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AttachMoneyIcon fontSize="small" color="action" />
                                                                <Typography variant="caption" fontWeight={600}>
                                                                    {totalAmount}
                                                                </Typography>
                                                            </Box>
                                                            {invoice.status && (
                                                                <Chip
                                                                    label={invoice.status}
                                                                    size="small"
                                                                    color={getStatusColor(invoice.status)}
                                                                    sx={{ textTransform: 'capitalize' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <ArrowForwardIcon color="action" />
                                            </ListItemButton>
                                            {index < invoices.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </CardContent>
                    </Card>
                )}

                {invoices.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                            sx={{ color: 'primary.main' }}
                        >
                            Add another invoice
                        </Button>
                    </Box>
                )}

                {/* Invoice Form Modal */}
                <InvoiceForm
                    open={formOpen}
                    onClose={handleFormClose}
                    invoiceId={selectedInvoiceId}
                    companyId={company.id}
                    onSaved={handleInvoiceSaved}
                    onSaveAndAddAnother={handleSaveAndAddAnother}
                />
            </Container>
        </section>
    );
}

export default InvoicesPage;
